import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useBankTransactionsAccess } from '@features/bankTransactions/hooks/useBankTransactionsAccess'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useUmbrella } from '@modules-deprecated/app/umbrellas'

import { NavigationMenuId } from '../enums/navigationMenuId'
import { NavigationMenuItemId } from '../types/NavigationMenuItemId'
import { transformItemsStructure } from '../utils/transformItemsStructure'
import { useConvertEmberRouteToUrl } from './useConverEmberRouteToUrl'
import { useNavigationMenuFilters } from './useNavigationMenuFilters'

export const useNavigationMenuItems = () => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { convertEmberRouteToUrl } = useConvertEmberRouteToUrl()
  const { filteredMenuItems } = useNavigationMenuFilters()
  const { umbrella } = useUmbrella()
  const { isTransactionsDefaultView, isTransactionsOnboardingEnabled, isTransactionsViewVisible } =
    useBankTransactionsAccess()

  const organizationUrl = organization?.url

  const bankingDefaultSubItemIds = useMemo((): NavigationMenuItemId[] => {
    // Transactions-first users land on Accounts by default when clicking "Banking", instead of the
    // generic "first visible submenu item" convention — which would otherwise be Transactions,
    // since it's first in navigationMenuConfig. The submenu itself is unchanged either way.
    if (isTransactionsOnboardingEnabled) {
      return [NavigationMenuId.BankAccounts]
    }

    // Users who see both views but keep the old reconciliation as default (accountants in new organizations)
    // must not land on Transactions just because it comes first in navigationMenuConfig.
    if (isTransactionsViewVisible && !isTransactionsDefaultView) {
      return [NavigationMenuId.Reconciliation, NavigationMenuId.NoBankAccounts]
    }

    return []
  }, [isTransactionsDefaultView, isTransactionsOnboardingEnabled, isTransactionsViewVisible])

  const transformedItemsStructure = useMemo(() => {
    if (!organizationUrl && !umbrella) {
      return []
    }

    const items = transformItemsStructure({ menuItems: filteredMenuItems, convertEmberRouteToUrl, organizationUrl, t })

    if (!bankingDefaultSubItemIds.length) {
      return items
    }

    return items.map((item) => {
      if (item.id !== NavigationMenuId.Banking || !item.subItems) {
        return item
      }

      const defaultSubItem = item.subItems.find((subItem) => bankingDefaultSubItemIds.includes(subItem.id))

      return defaultSubItem ? { ...item, to: defaultSubItem.to, value: defaultSubItem.value } : item
    })
  }, [bankingDefaultSubItemIds, convertEmberRouteToUrl, filteredMenuItems, organizationUrl, t, umbrella])

  return { navigationMenuItems: transformedItemsStructure }
}
